import { Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { TableComponent } from '@componentes/table/table.component';
import {
	Estacao,
	Municipio,
} from '@modulos/home/submodulos/dados-meteorologicos/interfaces/filtros-opcoes';
import {
	PostosRelatorios,
	RelatorioPrecipitacaoAnual,
} from '@modulos/home/submodulos/dados-meteorologicos/interfaces/tabela-relatorio';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	InstanciaRelatorio,
} from '@modulos/home/submodulos/dados-meteorologicos/interfaces/tipos-relatorios';
import { RelatoriosService } from '@modulos/home/submodulos/dados-meteorologicos/services/relatorios.service';
import { Select } from '@layout/interfaces/select';
import {
	alinharDado,
	baixarEAbrirPDF,
	isNotNuloOuUndefined,
	numberToBrNumber,
	renderCoordenada,
} from '@utils';
import { DateTimeUtils } from '@utils/datetime-util';
import { DocumentExporter } from '@utils/document-exporter';
import * as pdfseira from '@utils/pdf-seira';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { corrigeDuplicacaoNome } from '@modulos/home/submodulos/dados-meteorologicos/utils';
import * as ExcelTable from 'mr-excel';
import { DataModel } from 'mr-excel';
import { TipoTabela } from '../../../interfaces';

@Component({
	selector: 'seira-pluviometro-tabela-precipitacao-anual',
	templateUrl: './pluviometro-tabela-pluv-anual-personalizada.component.html',
	styleUrls: ['./pluviometro-tabela-pluv-anual-personalizada.component.scss'],
})
export class PluviometroTabelaPrecipitacaoAnualComponent
	implements InstanciaRelatorio, OnDestroy
{
	tituloRelatorio = TipoTabela.PRECIPITACAO_ANUAL + ' (mm)';
	descricaoRelatorio =
		'Define-se como pluviometria anual personalizada, o total pluviométrico acumulado no período de um ano.';

	postos: PostosRelatorios[] = [];
	estacoes: Estacao[] = [];
	inputs = inject(INPUTS_RELATORIOS);
	dadosTabelaPrecipitacaoAnual?: RelatorioPrecipitacaoAnual;
	carregandoRelatorio = true;
	private subscription = new Subscription();
	microrregioes: Select[] = [];
	periodo = '';
	@ViewChild('tabela-precipitacao-anual') tabela!: TableComponent;

	opcoesColunas = [
		{ name: 'Latitude', isFixed: false },
		{ name: 'Longitude', isFixed: false },
		{ name: 'Altitude', isFixed: false },
		{ name: 'Microrregião', isFixed: false },
		{ name: 'Mesorregião', isFixed: false },
		{ name: 'Região Pluviométrica', isFixed: false },
		{ name: 'Bacia/Sub-bacia', isFixed: false },
	];
	colunasSelecionadas: string[] = [];

	botoesDeExportacao: GroupButton[] = [
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () => {
				this.exportarPDF(this.dadosTabelaPrecipitacaoAnual!);
			},
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => {
				this.exportarCSV(this.dadosTabelaPrecipitacaoAnual!);
			},
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => {
				this.exportarTXT(this.dadosTabelaPrecipitacaoAnual!);
			},
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];

	form?: FormGroup<any> | undefined;
	regioes?: Select<string>[] | undefined;
	mesorregioes?: Select<string>[] | undefined;
	municipios?: Municipio[] | undefined;
	precipitacao?: Select<string>[] | undefined;

	constructor(
		private toastr: ToastrService,
		private relatoriosService: RelatoriosService
	) {
		this.postos = this.inputs.postos;
		this.estacoes = this.inputs.estacoes;
		this.setValidators();
	}

	setColunasSelecionadas(colunas: string[]) {
		this.colunasSelecionadas = colunas;
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.PERIODO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.POSTO)
			?.setValidators(Validators.required);
	}

	ngOnDestroy() {
		this.inputs.form.get(FormularioRelatorio.PERIODO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.POSTO)?.clearValidators();
		this.subscription.unsubscribe();
	}

	getFormItemValue(field: string) {
		return this.inputs.form.get(field)!.value;
	}

	public gerarRelatorio() {
		if (this.inputs.form.invalid) {
			return;
		}

		this.carregandoRelatorio = true;
		this.inputs.setLoading(true);

		const postosIds = this.postos.map(posto => posto.id);

		const dataFim = this.getFormItemValue('periodo');

		this.periodo = DateTimeUtils.formatarDataPeriodo(dataFim, '', '', true);

		this.tituloRelatorio =
			TipoTabela.PRECIPITACAO_ANUAL + ' (mm) - ' + this.periodo;

		this.relatoriosService
			.relatorioPrecipitacaoAnual(postosIds, null, dataFim.toISOString())
			.subscribe({
				next: prec => {
					prec == null
						? this.relatoriosService.setExibirBotoesExportacao(false)
						: this.relatoriosService.setExibirBotoesExportacao(true);
					this.dadosTabelaPrecipitacaoAnual = prec;
					this.dadosTabelaPrecipitacaoAnual.dataBusca = dataFim;
				},
				error: () => {
					this.carregandoRelatorio = false;
					this.toastr.error('Ocorreu um erro ao gerar o relatório');
					this.inputs.setLoading(false);
				},
				complete: () => {
					this.inputs.setLoading(false);
					this.carregandoRelatorio = false;
				},
			});
	}

	getPeriodo() {
		return DateTimeUtils.formatarDataPeriodo(
			new Date(this.inputs.form.get('periodo')?.value),
			'',
			'anual',
			true
		);
	}

	retornarPrecipitacaoAnualParaXLSX() {
		const tableData: any[] = [];

		this.dadosTabelaPrecipitacaoAnual?.chuvasAcumuladas.forEach(
			(dadoRelatorio: any) => {
				const item: any = {
					municipioPosto: corrigeDuplicacaoNome(
						dadoRelatorio.detalhesPosto.municipio +
							'/' +
							dadoRelatorio.detalhesPosto.posto
					),
				};
				if (this.colunasSelecionadas.includes('Latitude')) {
					item.latitude = renderCoordenada(dadoRelatorio.detalhesPosto?.latitude?.toString());
				}
				if (this.colunasSelecionadas.includes('Longitude')) {
					item.longitude = renderCoordenada(dadoRelatorio.detalhesPosto?.longitude?.toString());
				}
				if (this.colunasSelecionadas.includes('Altitude')) {
					item.altitude = renderCoordenada(dadoRelatorio.detalhesPosto?.altitude?.toString(), 1);
				}
				if (this.colunasSelecionadas.includes('Microrregião')) {
					item.microrregiao = dadoRelatorio.detalhesPosto?.microrregiao || '-';
				}
				if (this.colunasSelecionadas.includes('Mesorregião')) {
					item.mesorregiao = dadoRelatorio.detalhesPosto?.mesorregiao || '-';
				}
				if (this.colunasSelecionadas.includes('Região Pluviométrica')) {
					item.regiao = dadoRelatorio.detalhesPosto?.regiaoPluviometrica || dadoRelatorio.detalhesPosto?.regiao || '-';
				}
				if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) {
					item.bacia = dadoRelatorio.detalhesPosto?.bacia || '-';
				}
				item.tipoEstacao =
					dadoRelatorio.tipoEstacao === 'PLUVIOMETRO_CONVENCIONAL'
						? 'PCO'
						: 'PCD';
				item.precipitacaoAcumulada = isNotNuloOuUndefined(
					dadoRelatorio.precipitacaoAcumulada
				)
					? Number(Number(dadoRelatorio.precipitacaoAcumulada).toFixed(1))
					: undefined;

				tableData.push(item);
			}
		);

		return tableData;
	}

	async exportarPDF(dados: RelatorioPrecipitacaoAnual) {
		const documentDefinition: any = await pdfseira.documentDefinitions(
			'landscape',
			'A3'
		);

		documentDefinition.content.push({
			text: `Pluviometria anual personalizada (mm) - ${this.getPeriodo()}`,
			fontSize: 12,
			alignment: 'center',
			margin: [0, 10],
		});

		if (!dados || !dados.chuvasAcumuladas?.length) {
			documentDefinition.content.push({
				text: 'Nenhum dado encontrado na tabela',
				alignment: 'center',
				fontSize: 10,
				margin: [0, 10],
			});
		}

		const colunasPersonalizadasHeader = [];
		if (this.colunasSelecionadas.includes('Latitude')) colunasPersonalizadasHeader.push('Latitude');
		if (this.colunasSelecionadas.includes('Longitude')) colunasPersonalizadasHeader.push('Longitude');
		if (this.colunasSelecionadas.includes('Altitude')) colunasPersonalizadasHeader.push('Altitude');
		if (this.colunasSelecionadas.includes('Microrregião')) colunasPersonalizadasHeader.push('Microrregião');
		if (this.colunasSelecionadas.includes('Mesorregião')) colunasPersonalizadasHeader.push('Mesorregião');
		if (this.colunasSelecionadas.includes('Região Pluviométrica')) colunasPersonalizadasHeader.push('Região Pluviométrica');
		if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) colunasPersonalizadasHeader.push('Bacia/Sub-bacia');

		const headersPDF = [
			'Município/Posto',
			...colunasPersonalizadasHeader,
			'Tipo de estação',
			'Pluviometria observada (mm)',
		];

		const numCols = headersPDF.length;
		const fontSize = numCols > 7 ? 7.5 : numCols > 4 ? 8.5 : 9;

		const widths = headersPDF.map(col => {
			if (numCols <= 5) {
				if (col === 'Tipo de estação') return 120;
				if (col === 'Pluviometria observada (mm)') return 150;
				return '*';
			}
			if (col === 'Tipo de estação') return 70;
			if (col === 'Pluviometria observada (mm)') return 85;
			if (col === 'Latitude') return 60;
			if (col === 'Longitude') return 60;
			if (col === 'Altitude') return 45;
			return '*';
		});

		const tableData: any[][] = [];

		tableData.push(
			headersPDF.map(col => ({
				text: col,
				fillColor: '#DCDCDC',
				fontSize: fontSize,
				alignment: 'center',
				bold: true,
			}))
		);

		dados?.chuvasAcumuladas?.forEach((dadoRelatorio: any) => {
			const row: any[] = [];
			const nomeMunPosto = corrigeDuplicacaoNome(
				`${dadoRelatorio.detalhesPosto.municipio}/${dadoRelatorio.detalhesPosto.posto}`
			).replace('/', ' / ');
			row.push({
				text: nomeMunPosto,
				alignment: 'left',
				fontSize,
			});

			if (this.colunasSelecionadas.includes('Latitude')) {
				row.push({
					text: renderCoordenada(dadoRelatorio.detalhesPosto?.latitude?.toString()),
					alignment: 'center',
					fontSize,
				});
			}
			if (this.colunasSelecionadas.includes('Longitude')) {
				row.push({
					text: renderCoordenada(dadoRelatorio.detalhesPosto?.longitude?.toString()),
					alignment: 'center',
					fontSize,
				});
			}
			if (this.colunasSelecionadas.includes('Altitude')) {
				row.push({
					text: renderCoordenada(dadoRelatorio.detalhesPosto?.altitude?.toString(), 1),
					alignment: 'center',
					fontSize,
				});
			}
			if (this.colunasSelecionadas.includes('Microrregião')) {
				row.push({
					text: dadoRelatorio.detalhesPosto?.microrregiao || '-',
					alignment: 'center',
					fontSize,
				});
			}
			if (this.colunasSelecionadas.includes('Mesorregião')) {
				row.push({
					text: dadoRelatorio.detalhesPosto?.mesorregiao || '-',
					alignment: 'center',
					fontSize,
				});
			}
			if (this.colunasSelecionadas.includes('Região Pluviométrica')) {
				row.push({
					text:
						dadoRelatorio.detalhesPosto?.regiaoPluviometrica ||
						dadoRelatorio.detalhesPosto?.regiao ||
						'-',
					alignment: 'center',
					fontSize,
				});
			}
			if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) {
				row.push({
					text: dadoRelatorio.detalhesPosto?.bacia || '-',
					alignment: 'center',
					fontSize,
				});
			}

			row.push({
				text:
					dadoRelatorio.tipoEstacao === 'PLUVIOMETRO_CONVENCIONAL' ? 'PCO' : 'PCD',
				alignment: 'center',
				fontSize,
			});

			row.push({
				text: isNotNuloOuUndefined(dadoRelatorio.precipitacaoAcumulada)
					? numberToBrNumber(dadoRelatorio.precipitacaoAcumulada, 1)
					: '-',
				alignment: 'center',
				fontSize,
			});

			tableData.push(row);
		});

		documentDefinition.content.push({
			table: {
				headerRows: 1,
				widths: widths,
				body: tableData,
			},
			layout: {
				fillColor: (rowIndex: number) => (rowIndex === 0 ? '#DCDCDC' : null),
				paddingLeft: () => 3,
				paddingRight: () => 3,
				paddingTop: () => 3,
				paddingBottom: () => 3,
			},
		});

		baixarEAbrirPDF(
			`relatorio-pluviometria-anual-personalizada-${this.getPeriodo()}`,
			documentDefinition
		);
	}

	exportarCSV(dados: RelatorioPrecipitacaoAnual) {
		const tableData = this.setupTabelaPrecipitacao(dados);
		DocumentExporter.gerarCSV(
			tableData,
			`relatorio-pluviometria-anual-personalizada-${this.getPeriodo()}`
		);
	}

	exportarTXT(dados: RelatorioPrecipitacaoAnual) {
		let txtData = '';

		dados?.chuvasAcumuladas?.forEach((dadoRelatorio: any) => {
			txtData +=
				`Município/Posto: ${corrigeDuplicacaoNome(
					`${dadoRelatorio.detalhesPosto.municipio}/${dadoRelatorio.detalhesPosto.posto}`
				)}\n`;

			if (this.colunasSelecionadas.includes('Latitude')) {
				txtData += `Latitude: ${renderCoordenada(dadoRelatorio.detalhesPosto?.latitude?.toString())}\n`;
			}
			if (this.colunasSelecionadas.includes('Longitude')) {
				txtData += `Longitude: ${renderCoordenada(dadoRelatorio.detalhesPosto?.longitude?.toString())}\n`;
			}
			if (this.colunasSelecionadas.includes('Altitude')) {
				txtData += `Altitude: ${renderCoordenada(dadoRelatorio.detalhesPosto?.altitude?.toString(), 1)}\n`;
			}
			if (this.colunasSelecionadas.includes('Microrregião')) {
				txtData += `Microrregião: ${String(dadoRelatorio.detalhesPosto?.microrregiao || '-')}\n`;
			}
			if (this.colunasSelecionadas.includes('Mesorregião')) {
				txtData += `Mesorregião: ${String(dadoRelatorio.detalhesPosto?.mesorregiao || '-')}\n`;
			}
			if (this.colunasSelecionadas.includes('Região Pluviométrica')) {
				txtData += `Região Pluviométrica: ${String(dadoRelatorio.detalhesPosto?.regiaoPluviometrica || dadoRelatorio.detalhesPosto?.regiao || '-')}\n`;
			}
			if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) {
				txtData += `Bacia/Sub-bacia: ${String(dadoRelatorio.detalhesPosto?.bacia || '-')}\n`;
			}

			txtData +=
				`Tipo de estação: ${
					dadoRelatorio.tipoEstacao === 'PLUVIOMETRO_CONVENCIONAL'
						? 'PCO'
						: 'PCD'
				}\n` +
				`Pluviometria anual personalizada (mm): ${
					isNotNuloOuUndefined(dadoRelatorio.precipitacaoAcumulada)
						? numberToBrNumber(dadoRelatorio.precipitacaoAcumulada, 1)
						: '-'
				}\n\n`;
		});

		DocumentExporter.gerarTXT(
			txtData,
			`relatorio-pluviometria-anual-personalizada-${this.getPeriodo()}`
		);
	}

	exportarXLSX() {
		const colorPalette = {
			c1: '2C3639',
			c2: 'FFFFFF',
			c3: '000000',
			c4: 'EEEEEE',
		};
		const headerStyle = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
		};
		const headerStyleCenter = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as DataModel.AlignmentOption,
		};
		const rowStyleCenter = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as DataModel.AlignmentOption,
		};
		const rowStyleDecimal1 = {
			...rowStyleCenter,
			format: 'num_sep_1',
		};
		const rowStyleLeft = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'left',
				vertical: 'center',
			} as DataModel.AlignmentOption,
		};
		const title = {
			backgroundColor: colorPalette.c2,
			whiteSpace: 'pre',
			color: colorPalette.c3,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
				wrapText: 1,
			} as DataModel.AlignmentOption,
		};
		const headers: any[] = [
			{
				label: 'municipioPosto',
				text: 'Município/Posto',
				size: 24,
			},
		];
		if (this.colunasSelecionadas.includes('Latitude')) {
			headers.push({ label: 'latitude', text: 'Latitude', size: 15 });
		}
		if (this.colunasSelecionadas.includes('Longitude')) {
			headers.push({ label: 'longitude', text: 'Longitude', size: 15 });
		}
		if (this.colunasSelecionadas.includes('Altitude')) {
			headers.push({ label: 'altitude', text: 'Altitude', size: 15 });
		}
		if (this.colunasSelecionadas.includes('Microrregião')) {
			headers.push({ label: 'microrregiao', text: 'Microrregião', size: 20 });
		}
		if (this.colunasSelecionadas.includes('Mesorregião')) {
			headers.push({ label: 'mesorregiao', text: 'Mesorregião', size: 20 });
		}
		if (this.colunasSelecionadas.includes('Região Pluviométrica')) {
			headers.push({ label: 'regiao', text: 'Região Pluviométrica', size: 20 });
		}
		if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) {
			headers.push({ label: 'bacia', text: 'Bacia/Sub-bacia', size: 20 });
		}
		headers.push(
			{ label: 'tipoEstacao', text: 'Tipo de estação', size: 18 },
			{
				label: 'precipitacaoAcumulada',
				text: 'Pluviometria anual (mm)',
				size: 24,
			}
		);

		const dataExcel = {
			styles: {
				headerStyle,
				headerStyleCenter,
				rowStyleCenter,
				rowStyleDecimal1,
				rowStyleLeft,
				title,
			},
			sheet: [
				{
					shiftTop: 3,
					images: [
						{
							url: 'assets/images/cabecalho/cabeçalho_excel.png',
							from: 'A1',
							to: 'C3',
						},
					],
					styleCellCondition: (
						data: any,
						object: any,
						rowIndex: number,
						colIndex: number
					) => {
						if (data?.label) {
							return 'headerStyleCenter';
						}
						const label = headers[colIndex]?.label;
						if (
							label === 'municipioPosto' ||
							label === 'microrregiao' ||
							label === 'mesorregiao' ||
							label === 'regiao' ||
							label === 'bacia'
						) {
							return 'rowStyleLeft';
						}
						if (label === 'precipitacaoAcumulada') {
							return 'rowStyleDecimal1';
						}
						return 'rowStyleCenter';
					},
					headers: headers,
					data: this.retornarPrecipitacaoAnualParaXLSX(),
					convertStringToNumber: true,
					title: {
						consommeRow: 3,
						consommeCol: 3,
						text: `Pluviometria anual personalizada - ${this.getPeriodo()}`,
						styleId: 'title',
					},
				},
			],
			fileName: `relatorio-pluviometria-anual-personalizada-${this.getPeriodo()}`,
		};

		ExcelTable.generateExcel(dataExcel);
	}

	setupTabelaPrecipitacao(dados: RelatorioPrecipitacaoAnual) {
		const tableData: (string | number)[][] = [];

		const colunasPersonalizadasHeader = [];
		if (this.colunasSelecionadas.includes('Latitude')) colunasPersonalizadasHeader.push('Latitude');
		if (this.colunasSelecionadas.includes('Longitude')) colunasPersonalizadasHeader.push('Longitude');
		if (this.colunasSelecionadas.includes('Altitude')) colunasPersonalizadasHeader.push('Altitude');
		if (this.colunasSelecionadas.includes('Microrregião')) colunasPersonalizadasHeader.push('Microrregião');
		if (this.colunasSelecionadas.includes('Mesorregião')) colunasPersonalizadasHeader.push('Mesorregião');
		if (this.colunasSelecionadas.includes('Região Pluviométrica')) colunasPersonalizadasHeader.push('Região Pluviométrica');
		if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) colunasPersonalizadasHeader.push('Bacia/Sub-bacia');

		tableData.push([
			'Município/Posto',
			...colunasPersonalizadasHeader,
			'Tipo de estação',
			'Pluviometria anual personalizada (mm)',
		]);

		const formatar = (v: any) =>
			isNotNuloOuUndefined(v) ? Number(v).toFixed(1).replace('.', ',') : '-';

		dados?.chuvasAcumuladas?.forEach((dadoRelatorio: any) => {
			const colunasPersonalizadasLinha: string[] = [];
			if (this.colunasSelecionadas.includes('Latitude')) {
				colunasPersonalizadasLinha.push(renderCoordenada(dadoRelatorio.detalhesPosto?.latitude?.toString()));
			}
			if (this.colunasSelecionadas.includes('Longitude')) {
				colunasPersonalizadasLinha.push(renderCoordenada(dadoRelatorio.detalhesPosto?.longitude?.toString()));
			}
			if (this.colunasSelecionadas.includes('Altitude')) {
				colunasPersonalizadasLinha.push(renderCoordenada(dadoRelatorio.detalhesPosto?.altitude?.toString(), 1));
			}
			if (this.colunasSelecionadas.includes('Microrregião')) {
				colunasPersonalizadasLinha.push(String(dadoRelatorio.detalhesPosto?.microrregiao || '-'));
			}
			if (this.colunasSelecionadas.includes('Mesorregião')) {
				colunasPersonalizadasLinha.push(String(dadoRelatorio.detalhesPosto?.mesorregiao || '-'));
			}
			if (this.colunasSelecionadas.includes('Região Pluviométrica')) {
				colunasPersonalizadasLinha.push(String(dadoRelatorio.detalhesPosto?.regiaoPluviometrica || dadoRelatorio.detalhesPosto?.regiao || '-'));
			}
			if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) {
				colunasPersonalizadasLinha.push(String(dadoRelatorio.detalhesPosto?.bacia || '-'));
			}

			tableData.push([
				corrigeDuplicacaoNome(
					`${dadoRelatorio.detalhesPosto.municipio}/${dadoRelatorio.detalhesPosto.posto}`
				),
				...colunasPersonalizadasLinha,
				dadoRelatorio.tipoEstacao === 'PLUVIOMETRO_CONVENCIONAL' ? 'PCO' : 'PCD',
				formatar(dadoRelatorio.precipitacaoAcumulada),
			]);
		});

		return tableData;
	}
}
